import * as React from "react";
import { Upload } from "antd";
import { ImportOutlined } from "@ant-design/icons";
import { useImport, UseImportOptions } from "./useImport";
import { InfoTooltip } from "../InfoTooltip";

export interface ImportButtonProps<TExisting, TResult> extends UseImportOptions<TExisting, TResult> {
  existingValue: TExisting;
  accept?: string;
  explanation?: React.ReactNode;
}

export function ImportButton<TExisting, TResult>({
  existingValue,
  accept = ".env,.yaml,.yml,.json",
  explanation,
  ...importOptions
}: ImportButtonProps<TExisting, TResult>) {
  const { importContent } = useImport<TExisting, TResult>(importOptions, existingValue);

  const uploadProps = {
    showUploadList: false,
    accept,
    beforeUpload: async (file: File) => {
      const content = await file.text();
      importContent(content, file.name);
      return false as const;
    },
  };

  const [isDragOver, setIsDragOver] = React.useState(false);
  const dragCounter = React.useRef(0);

  return (
    <div className="inline-flex items-center">
      <Upload {...uploadProps} className="inline-block [&_.ant-upload]:block">
        <div
          className="flex items-center gap-2 px-3 py-1.5 border border-dashed rounded cursor-pointer transition-all text-sm"
          style={{
            borderColor: isDragOver ? "var(--color-primary)" : "var(--color-border)",
            color: isDragOver ? "var(--color-primary)" : "var(--color-label)",
            backgroundColor: isDragOver ? "var(--color-primary-faint)" : "transparent",
          }}
          onMouseEnter={(e) => {
            if (!isDragOver) {
              e.currentTarget.style.borderColor = "var(--color-primary)";
              e.currentTarget.style.color = "var(--color-primary)";
            }
          }}
          onMouseLeave={(e) => {
            if (!isDragOver) {
              e.currentTarget.style.borderColor = "var(--color-border)";
              e.currentTarget.style.color = "var(--color-label)";
            }
          }}
          onDragEnter={(e) => {
            e.preventDefault();
            dragCounter.current++;
            setIsDragOver(true);
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => {
            dragCounter.current--;
            if (dragCounter.current === 0) setIsDragOver(false);
          }}
          onDrop={() => {
            dragCounter.current = 0;
            setIsDragOver(false);
          }}
        >
          <ImportOutlined />
          <span>Drop file or click to import</span>
        </div>
      </Upload>
      {explanation && <InfoTooltip title={explanation} style={{ marginLeft: 8 }} />}
    </div>
  );
}
